import LoadingButton from 'components/LoadingButton/LoadingButton';
import ConfirmationModal from 'components/Modals/ConfirmationModal/ConfirmationModal';
import { useEffect, useRef, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import {
    GET_CAMPAIGNS,
    GET_PROPERTIES,
    GET_CAMPAIGN_PROPERTIES_FOR_CAMPAIGN,
    useLazyQuery,
    useQuery,
    useMutation,
    CREATE_CAMPAIGN,
    CREATE_CAMPAIGN_PROPERTIES,
    DELETE_CAMPAIGN,
    DELETE_CAMPAIGN_PROPERTIES,
    UPDATE_CAMPAIGN,
    GET_ORDERS_BY_CAMPAIGN_ID,
} from 'utils/graphql';
import { ReactComponent as CheckIcon } from 'assets/icons/check_icon.svg';
import { ReactComponent as ErrorIcon } from 'assets/icons/error_icon.svg';
import { ReactComponent as WarningIcon } from 'assets/icons/exclamation-triangle.svg';
import './CampaignsSettings.css';
import { formatUTCDateTime } from 'utils/date.util';
import InfoModal from 'components/Modals/InfoModal/InfoModal';

const CampaignsSettings = () => {
    const { t } = useTranslation();
    const [campaigns, setCampaigns] = useState([]);
    const [totalCampaigns, setTotalCampaigns] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [searchFilter, setSearchFilter] = useState('');
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [orderBy, setOrderBy] = useState('name');
    const [orderByDirection, setOrderByDirection] = useState('asc');

    const [validated, setValidated] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [campaignToEdit, setCampaignToEdit] = useState(null);
    const [campaignToDelete, setCampaignToDelete] = useState(null);
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState(false);
    const [properties, setProperties] = useState([]);

    const [name, setName] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [description, setDescription] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [campaignPropertyIds, setCampaignPropertyIds] = useState([]);
    const [formErrors, setFormErrors] = useState({
        name: '',
        campaignPropertyIds: '',
        campaign: '',
    });

    const [infoModalText, setInfoModalText] = useState('');
    const [infoModalOptions, setInfoModalOptions] = useState({});
    const [infoModalIsOpen, setInfoModalIsOpen] = useState(false);

    const firstUpdate = useRef(true);

    const { loading: isLoadingProperties } = useQuery(GET_PROPERTIES, {
        onCompleted: (data) => {
            setProperties(data.table.records.map((record) => record.property__c));
        },
    });

    const [getCampaignProperties, { loading: isLoadingCampaignProperties }] = useLazyQuery(
        GET_CAMPAIGN_PROPERTIES_FOR_CAMPAIGN,
        {
            fetchPolicy: 'cache-and-network',
        }
    );

    const [getCampaigns] = useLazyQuery(GET_CAMPAIGNS, {
        fetchPolicy: 'cache-and-network',
    });

    const [getOrdersForCampaign] = useLazyQuery(GET_ORDERS_BY_CAMPAIGN_ID, {
        fetchPolicy: 'cache-and-network',
    });

    const [createCampaignMutation] = useMutation(CREATE_CAMPAIGN);
    const [updateCampaignMutation] = useMutation(UPDATE_CAMPAIGN);
    const [createCampaignPropertiesMutation] = useMutation(CREATE_CAMPAIGN_PROPERTIES);
    const [deleteCampaignMutation] = useMutation(DELETE_CAMPAIGN);
    const [deleteCampaignPropertiesMutation] = useMutation(DELETE_CAMPAIGN_PROPERTIES);

    const isCampaignBeingDeleted = () => {
        return campaigns.some((campaign) => campaign.isDeleting);
    };

    const openFormModal = (campaignToEdit) => {
        setCampaignToEdit(campaignToEdit);
        setShowFormModal(true);
        setFormValues(campaignToEdit);
    };

    const setFormValues = (campaign = null) => {
        setIsFormSubmitting(false);
        setValidated(false);
        if (campaign) {
            setName(campaign.name);
            setStartDate(
                campaign.startdate ? formatUTCDateTime(new Date(campaign.startdate), false) : ''
            );
            setEndDate(
                campaign.enddate ? formatUTCDateTime(new Date(campaign.enddate), false) : ''
            );
            setDescription(campaign.description ? campaign.description : '');
            setIsActive(campaign.isactive);
            getCampaignProperties({
                variables: {
                    campaignId: campaign.sfid,
                },
                onCompleted: (data) => {
                    const campaignProperties = data.table.records.map(
                        (record) => record.campaign_property
                    );
                    setCampaignPropertyIds(campaignProperties.map((cp) => cp.property_id));
                    setCampaignToEdit({
                        ...campaign,
                        campaignProperties,
                    });
                },
            });
        } else {
            setName('');
            setStartDate('');
            setEndDate('');
            setCampaignPropertyIds([]);
            setDescription('');
            setIsActive(true);
        }
    };

    const columns = [
        {
            name: t('name'),
            sortable: true,
            id: 'name',
            minWidth: '250px',
            selector: (row) => row.name,
        },
        {
            name: t('description'),
            sortable: true,
            id: 'description',
            minWidth: '250px',
            selector: (row) => row.description,
        },
        {
            name: t('start_date'),
            sortable: true,
            id: 'startdate',
            selector: (row) => row.startdate,
        },
        {
            name: t('end_date'),
            sortable: true,
            id: 'enddate',
            selector: (row) => row.enddate,
        },
        {
            name: t('active'),
            sortable: true,
            id: 'isactive',
            maxWidth: '85px',
            selector: (row) => (row.isactive ? t('true') : t('false')),
        },
        {
            name: t('manage'),
            sortable: false,
            id: 'manage',
            minWidth: '235px',
            selector: (row) => {
                if (row.islocal) {
                    return (
                        <div className="d-flex align-items-center">
                            <Button
                                className="me-1"
                                variant="success"
                                disabled={isCampaignBeingDeleted()}
                                onClick={() => openFormModal(row)}
                            >
                                {t('edit')}
                            </Button>
                            <LoadingButton
                                isLoading={row.isDeleting}
                                isDisabled={isCampaignBeingDeleted()}
                                variant="danger"
                                onClick={() => {
                                    setCampaignToDelete(row);
                                    setIsDeleteConfirmationModalOpen(true);
                                }}
                            >
                                {t('delete')}
                            </LoadingButton>
                        </div>
                    );
                } else {
                    return 'N/A';
                }
            },
        },
    ];

    const datatableOnSort = (selectedColumn, sortDirection, sortedRows) => {
        if (selectedColumn && selectedColumn.id) {
            setOrderBy(selectedColumn.id);
            setOrderByDirection(sortDirection);
        }
    };

    const loadCampaigns = async () => {
        setIsLoading(true);
        const { data, error } = await getCampaigns({
            variables: {
                offset: (page - 1) * rowsPerPage,
                limit: rowsPerPage,
                orderBy,
                orderByDirection: orderByDirection.toUpperCase(),
                filter: searchFilter ? `%${searchFilter}%` : '%',
            },
        });
        if (data && data.table) {
            setTotalCampaigns(data.table.count);
            setCampaigns(
                data.table.records.map((record) => {
                    return {
                        ...record.campaign,
                        startdate: record.campaign.startdate
                            ? formatUTCDateTime(new Date(record.campaign.startdate), false)
                            : '',
                        enddate: record.campaign.enddate
                            ? formatUTCDateTime(new Date(record.campaign.enddate), false)
                            : '',
                        isDeleting: false,
                    };
                })
            );
            if (firstUpdate.current) {
                firstUpdate.current = false;
            }
        } else {
            console.log(error);
        }
        setIsLoading(false);
    };

    const showInfoModal = (text, success = true) => {
        setInfoModalText(text);
        setInfoModalOptions({
            header: success ? (
                <CheckIcon fill="var(--bs-green)" />
            ) : (
                <ErrorIcon fill="var(--bs-red)" />
            ),
            center: true,
        });
        setInfoModalIsOpen(true);
    };

    const validate = async () => {
        let _formErrors = {
            name: '',
            campaignPropertyIds: '',
            campaign: '',
        };
        let errors = false;

        if (!name) {
            _formErrors.name = t('name_empty_error');
            errors = true;
        }

        if (!campaignPropertyIds || campaignPropertyIds.length === 0) {
            _formErrors.campaignPropertyIds = t('property_selection_mandatory_error');
            errors = true;
        }

        setFormErrors(_formErrors);
        if (errors) {
            setValidated(true);
            return false;
        }
        return true;
    };

    const showCampaignFormError = (error) => {
        setFormErrors({
            name: '',
            campaignPropertyIds: '',
            campaign: error,
        });
        setValidated(true);
    };

    const createCampaign = async () => {
        setIsFormSubmitting(true);
        if (await validate()) {
            const date = formatUTCDateTime();
            const { data: createCampaignData, errors: createCampaignErrors } =
                await createCampaignMutation({
                    variables: {
                        name,
                        createddate: date,
                        startdate: startDate,
                        enddate: endDate,
                        description,
                        isactive: isActive,
                        sfid: uuidv4(),
                    },
                });
            if (createCampaignErrors) {
                showCampaignFormError(t('campaign_creation_fail'));
            } else {
                const rows = campaignPropertyIds.map((propertyId) => {
                    return {
                        values: [
                            {
                                property: 'campaign_id',
                                value: createCampaignData.createRow.campaign.sfid,
                            },
                            {
                                property: 'property_id',
                                value: propertyId,
                            },
                        ],
                    };
                });
                const {
                    data: createCampaignPropertiesData,
                    errors: createCampaignPropertiesErrors,
                } = await createCampaignPropertiesMutation({
                    variables: {
                        rows,
                    },
                });
                if (
                    createCampaignPropertiesErrors ||
                    (createCampaignPropertiesData.batchCreateRow.errors &&
                        createCampaignPropertiesData.batchCreateRow.errors.length > 0)
                ) {
                    showCampaignFormError(t('campaign_creation_fail'));
                    // rollback campaign creation
                    deleteCampaignMutation({
                        variables: { id: createCampaignData.createRow.campaign.id },
                    });
                } else {
                    setShowFormModal(false);
                    showInfoModal(t('campaign_creation_success'));
                    loadCampaigns();
                }
            }
            setIsFormSubmitting(false);
        } else {
            setIsFormSubmitting(false);
        }
    };

    const editCampaign = async () => {
        setIsFormSubmitting(true);
        if (await validate()) {
            const campaignPropertyIdsToCreate = campaignPropertyIds.filter(
                (propertyId) =>
                    !campaignToEdit.campaignProperties.find(
                        (campaignProperty) => campaignProperty.property_id === propertyId
                    )
            );
            const campaignPropertiesToDelete = campaignToEdit.campaignProperties.filter(
                (campaignProperty) =>
                    campaignPropertyIds.indexOf(campaignProperty.property_id) === -1
            );

            // creating new campaign_properties
            const rows = campaignPropertyIdsToCreate.map((propertyId) => {
                return {
                    values: [
                        {
                            property: 'campaign_id',
                            value: campaignToEdit.sfid,
                        },
                        {
                            property: 'property_id',
                            value: propertyId,
                        },
                    ],
                };
            });
            const { data: createCampaignPropertiesData, errors: createCampaignPropertiesErrors } =
                await createCampaignPropertiesMutation({
                    variables: {
                        rows,
                    },
                });

            if (
                createCampaignPropertiesErrors ||
                (createCampaignPropertiesData.batchCreateRow.errors &&
                    createCampaignPropertiesData.batchCreateRow.errors.length > 0)
            ) {
                showCampaignFormError(t('campaign_update_fail'));
                setIsFormSubmitting(false);
                return;
            }

            // deleting campaign_properties
            if (campaignPropertiesToDelete.length > 0) {
                const { errors: deleteCampaignPropertiesErrors } =
                    await deleteCampaignPropertiesMutation({
                        variables: {
                            ids: campaignPropertiesToDelete.map(
                                (campaignProperty) => campaignProperty.id
                            ),
                        },
                    });

                if (deleteCampaignPropertiesErrors) {
                    // rolling back campaign_property creation
                    const ids = createCampaignPropertiesData.batchCreateRow.campaign_property.map(
                        (campaign_property) => campaign_property.id
                    );
                    if (ids.length > 0) {
                        deleteCampaignPropertiesMutation({
                            variables: {
                                ids,
                            },
                        });
                    }
                    showCampaignFormError(t('campaign_update_fail'));
                    setIsFormSubmitting(false);
                    return;
                }
            }

            const { errors: updateCampaignErrors } = await updateCampaignMutation({
                variables: {
                    id: campaignToEdit.id,
                    name,
                    startdate: startDate,
                    enddate: endDate,
                    date: formatUTCDateTime(),
                    description,
                    isactive: isActive,
                },
            });

            if (updateCampaignErrors) {
                // rolling back campaign_property creation
                deleteCampaignPropertiesMutation({
                    variables: {
                        ids: createCampaignPropertiesData.batchCreateRow.campaign_property.map(
                            (campaign_property) => campaign_property.id
                        ),
                    },
                });
                // TODO: need to rollback the deleted campaign_properties
                showCampaignFormError(t('campaign_update_fail'));
                setIsFormSubmitting(false);
                return;
            }
            setShowFormModal(false);
            showInfoModal(t('campaign_update_success'));
            loadCampaigns();
        } else {
            setIsFormSubmitting(false);
        }
    };

    const deleteCampaign = async () => {
        campaignToDelete.isDeleting = true;

        const { data } = await getOrdersForCampaign({
            variables: { campaignid: campaignToDelete.id },
        });

        if (data.table.count > 0) {
            showInfoModal(t('campaign_delete_forbidden'), false);
            campaignToDelete.isDeleting = false;
            return;
        }

        const { data: getCampaignPropertiesData, erors: getCampaignPropertiesErrors } =
            await getCampaignProperties({
                variables: {
                    campaignId: campaignToDelete.sfid,
                },
            });

        if (getCampaignPropertiesErrors) {
            showInfoModal(t('campaign_delete_fail'));
            campaignToDelete.isDeleting = false;
            return;
        }

        // deleting campaign_properties
        if (getCampaignPropertiesData.table.count > 0) {
            const { errors: deleteCampaignPropertiesErrors } =
                await deleteCampaignPropertiesMutation({
                    variables: {
                        ids: getCampaignPropertiesData.table.records.map(
                            (record) => record.campaign_property.id
                        ),
                    },
                });

            if (deleteCampaignPropertiesErrors) {
                showInfoModal(t('campaign_delete_fail'));
                campaignToDelete.isDeleting = false;
                return;
            }
        }

        const { errors: deleteCampaignErrors } = await deleteCampaignMutation({
            variables: { id: campaignToDelete.id },
        });

        if (deleteCampaignErrors) {
            showInfoModal(t('campaign_delete_fail'));
            // TODO: need to rollback deleted campaign properties
        } else {
            showInfoModal(t('campaign_delete_success'));
            loadCampaigns();
        }
        campaignToDelete.isDeleting = false;
    };

    useEffect(() => {
        if (firstUpdate.current) return;
        const timer = setTimeout(loadCampaigns, 500);
        return () => {
            clearTimeout(timer);
        };
    }, [searchFilter]);

    useEffect(() => {
        loadCampaigns();
    }, [page, rowsPerPage, orderBy, orderByDirection]);

    useEffect(() => setValidated(false), [name, campaignPropertyIds]);

    return (
        <div className="campaign-settings">
            <Button
                className="w-100 mb-4"
                disabled={isCampaignBeingDeleted()}
                onClick={() => openFormModal()}
            >
                {t('create_campaign')}
            </Button>
            <div className="d-flex justify-content-end">
                <Form.Control
                    type="text"
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    placeholder={t('search')}
                    className="search"
                />
            </div>
            <DataTable
                columns={columns}
                data={campaigns}
                pagination
                paginationServer
                paginationRowsPerPageOptions={[10, 20, 30, 50]}
                paginationTotalRows={totalCampaigns}
                onChangePage={(page) => setPage(page)}
                onChangeRowsPerPage={(currentRowsPerPage) => setRowsPerPage(currentRowsPerPage)}
                progressPending={isLoading}
                progressComponent={
                    <div className="d-flex justify-content-center align-items-center my-4">
                        <Spinner></Spinner>
                    </div>
                }
                highlightOnHover
                persistTableHead
                sortServer
                onSort={(selectedColumn, sortDirection, sortedRows) =>
                    datatableOnSort(selectedColumn, sortDirection, sortedRows)
                }
                paginationComponentOptions={{ rowsPerPageText: t('rows_per_page') }}
            />
            <Modal show={showFormModal} onHide={() => setShowFormModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h5>{campaignToEdit ? t('edit_campaign') : t('create_campaign')}</h5>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>
                                <span className="required-field">*</span> {t('name')}
                            </Form.Label>
                            <Form.Control
                                isInvalid={validated && formErrors.name}
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />
                            {formErrors.name && (
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.name}
                                </Form.Control.Feedback>
                            )}
                        </Form.Group>
                        {isLoadingProperties || isLoadingCampaignProperties ? (
                            <div className="my-4 d-flex justify-content-center">
                                <Spinner></Spinner>
                            </div>
                        ) : (
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <span className="required-field">*</span> {t('properties')}
                                </Form.Label>
                                <Form.Control
                                    as="select"
                                    multiple
                                    isInvalid={validated && formErrors.campaignPropertyIds}
                                    value={campaignPropertyIds}
                                    onChange={(e) =>
                                        setCampaignPropertyIds(
                                            [].slice
                                                .call(e.target.selectedOptions)
                                                .map((item) => item.value)
                                        )
                                    }
                                >
                                    {properties.map((property, i) => (
                                        <option value={property.sfid} key={i}>
                                            {property.name}
                                        </option>
                                    ))}
                                </Form.Control>
                                {formErrors.campaignPropertyIds && (
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.campaignPropertyIds}
                                    </Form.Control.Feedback>
                                )}
                                <Form.Text className="text-muted">
                                    {t('ctrl_click_properties')}
                                </Form.Text>
                            </Form.Group>
                        )}
                        <Form.Group className="mb-3">
                            <Form.Label>{t('start_date')}</Form.Label>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => {
                                    setStartDate(e.target.value);
                                }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('end_date')}</Form.Label>
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => {
                                    setEndDate(e.target.value);
                                }}
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('description')}</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Form.Group>

                        <Form.Group className="d-flex align-items-center mb-3">
                            <Form.Check
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                            />
                            <Form.Label className="m-0 mt-1 ms-2">{t('active')}</Form.Label>
                        </Form.Group>
                        {validated && formErrors.campaign && (
                            <div
                                className="invalid-feedback mt-3 fw-bold"
                                style={{ display: formErrors.campaign ? 'block' : 'none' }}
                            >
                                {formErrors.campaign}
                            </div>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <LoadingButton
                        onClick={campaignToEdit ? editCampaign : createCampaign}
                        isLoading={isFormSubmitting}
                        isDisabled={isLoadingProperties || isLoadingCampaignProperties}
                    >
                        {campaignToEdit ? t('edit') : t('create')}
                    </LoadingButton>
                </Modal.Footer>
            </Modal>
            <ConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                setOpen={setIsDeleteConfirmationModalOpen}
                onConfirm={deleteCampaign}
                options={{
                    center: true,
                    header: <WarningIcon fill="var(--bs-yellow)" height={48} width={48} />,
                }}
            >
                <p>{t('delete_campaign_confirmation')}</p>
            </ConfirmationModal>
            <InfoModal
                isOpen={infoModalIsOpen}
                setOpen={setInfoModalIsOpen}
                options={infoModalOptions}
            >
                {infoModalText}
            </InfoModal>
        </div>
    );
};

export default CampaignsSettings;
